import { Vue, Options } from 'vue-class-component';
import { PropType, defineAsyncComponent } from 'vue';

import {
  PlantDetailDTO,
  SceneDTO,
  SceneType,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import plantsClient from '@/core/services/plants';
import { isDefaultScene } from '@/core/utils/utils';
import { NamingService } from '../../../../../core/services/naming.service';

@Options({
  name: 'SceneWizard',
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  components: {
    SceneWizardStep1: defineAsyncComponent(
      () => import('./SceneWizardStep1/SceneWizardStep1.vue')
    ),
    SceneWizardStep2: defineAsyncComponent(
      () => import('./SceneWizardStep2/SceneWizardStep2.vue')
    ),
    SceneWizardStep3: defineAsyncComponent(
      () => import('./SceneWizardStep3/SceneWizardStep3.vue')
    ),
    SceneWizardStep4: defineAsyncComponent(
      () => import('./SceneWizardStep4/SceneWizardStep4.vue')
    ),
    // To be defined
    // SceneWizardStep5: defineAsyncComponent(
    //   () => import('./SceneWizardStep5/SceneWizardStep5.vue')
    // ),
  },
})
export default class SceneWizard extends Vue {
  currentPlant!: PlantDetailDTO;

  isEdit = false;
  sceneId = '';
  activeStep = 0;
  nextDisabled = false;
  scene: SceneDTO | null = null;

  get steps() {
    return [
      {
        title: 'AppResource.Name',
        componentName: 'SceneWizardStep1',
        show: true,
      },
      {
        title: `AppResource.AddTheActions`,
        componentName: 'SceneWizardStep2',
        show: true,
      },
      {
        title: 'AppResource.AddTheTriggers',
        componentName: 'SceneWizardStep3',
        show: !isDefaultScene(this.scene),
      },
      {
        title: 'AppResource.AddTheConditions',
        componentName: 'SceneWizardStep4',
        show: !isDefaultScene(this.scene),
      },
      // To be defined
      // {
      //   title: 'scenesWizard.overview',
      //   componentName: 'SceneWizardStep5',
      // },
    ].filter((s) => s.show);
  }

  get currentStepTitle() {
    return this.$t(this.steps[this.activeStep].title);
  }

  get activeStepComponent() {
    return this.steps[this.activeStep].componentName;
  }

  get buttonSubmitLabel() {
    if (this.activeStep < this.steps.length - 1) {
      return this.$t('AppResource.Next');
    }

    return this.$t('AppResource.SaveAndExit');
  }

  get plantId() {
    if (this.currentPlant) {
      return this.currentPlant.desktopId;
    }

    return null;
  }

  async mounted() {
    try {
      this.sceneId = this.$route.params.id as string;
      this.isEdit = !!this.sceneId;

      if (this.isEdit) {
        this.fetchScene();
      } else {
        const sceneIndex = parseInt(
          this.$route.params.sceneIndex as string,
          10
        );

        const newSceneIndex =
          !Number.isNaN(sceneIndex) && sceneIndex !== 0 ? ` ${sceneIndex}` : '';

        this.scene = new SceneDTO({
          name: `${this.$t('AppResource.Scene')}${newSceneIndex}`,
          type: SceneType.Custom,
          triggers: [],
          actions: [],
          conditions: [],
        });
      }
    } catch (error) {
      console.log('Error reading scene id, redirecting to scenes page.');
      this.goToScenes();
      return;
    }
  }

  async fetchScene() {
    this.$store.dispatch(SET_APP_LOADING, true);

    try {
      this.scene = await plantsClient.scenesGET(
        this.currentPlant.desktopId as number,
        this.sceneId
      );
    } catch (error) {
      let messageError = '';
      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  goToScenes() {
    this.$router.push({ name: 'page-plant-scenes' });
  }

  handleButtonBackAction() {
    if (this.activeStep > 0) {
      this.nextDisabled = false;
      this.activeStep--;
    } else {
      this.goToScenes();
    }
  }

  async handleButtonSubmitAction() {
    if (this.activeStep < this.steps.length - 1) {
      this.activeStep++;
      return;
    }

    try {
      this.$store.dispatch(SET_APP_LOADING, true);

      if (this.isEdit && this.sceneId) {
        await plantsClient.scenesPUT(
          this.plantId as number,
          this.sceneId,
          this.scene as SceneDTO
        );
        this.goToScenes();
      } else {
        await plantsClient.scenesPOST(
          this.plantId as number,
          this.scene as SceneDTO
        );
        this.goToScenes();
      }
    } catch (error) {
      let messageError = '';
      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      NamingService.flushCache();
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  nextEnabledEvent(nextEnabled: boolean) {
    this.nextDisabled = !nextEnabled;
  }
}
